import { Location } from "@angular/common";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ComponentStore, tapResponse } from "@ngrx/component-store";
import { Subject } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { HotelComparableFormComponent } from "../form/hotel-comparable-form.component";
import { formControlValueChanges } from "src/app/core/v2/utils";
import { HotelComparableAPIService, HotelComparableAssetClass } from 'src/app/core/v3/asset-class/hotel'

interface State {
    assetClass: HotelComparableAssetClass | null
}

@Component({
    selector: 'app-hotel-comparable-edit',
    templateUrl: './hotel-comparable-edit.component.html',
    styleUrls: [
        '../../complex-btn.scss'
    ],
    providers: [ComponentStore]
})
export class HotelComparableEditComponent implements OnInit, OnDestroy {
    @ViewChild(HotelComparableFormComponent, { static: true })
    formComponent!: HotelComparableFormComponent

    formSelectionCtrl = new FormControl<number>(2)
    formType$ = formControlValueChanges(this.formSelectionCtrl).pipe(
        map(value => value == 2 ? 'simple' : 'complex')
    )

    assetClass$ = this._store.select(state => state.assetClass)
    title$ = this._store.select(
        this.assetClass$,
        (ac) => {
            if (!ac) return 'Add Hotel Property';
            return `Edit ${ac.ref_num} Hotel Property`
        }
    )
    backUrl$ = this._store.select(
        this.assetClass$,
        (ac) => {
            if (!ac) return '../list';
            return '../../list';
        }
    )

    private _onDestroy$ = new Subject<void>()

    constructor(
        private _store: ComponentStore<State>,
        private _activatedRoute: ActivatedRoute,
        private _location: Location,
        private _service: HotelComparableAPIService
    ) {
        this._store.setState({
            assetClass: null
        })

        this._activatedRoute.params.pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(params => {
            if (!params.id) return;
            const id = Number(params.id)
            this._fetch(id)
        })
    }

    ngOnInit(): void { }

    ngOnDestroy(): void {
        this._onDestroy$.next()
        this._onDestroy$.complete()
    }

    onSubmit(isComplete: boolean = true) {
        const result = this.formComponent.submit(isComplete)
        if (!result) return;
        const { ac: assetClass, fileList } = result
        if (assetClass.id) {
            this._update({ac: assetClass, fileList})
        } else {
            this._create({ ac: assetClass, fileList })
        }
    }

    private _fetch = this._store.effect<number>(id$ =>
        id$.pipe(
            switchMap(id =>
                this._service.fetch(id).pipe(
                    tapResponse(
                        (response) => {
                            this._store.patchState({ assetClass: response })
                            this.formSelectionCtrl.patchValue(response.form_type)
                        },
                        (err) => {
                            console.error(err)
                        }
                    )
                )
            )
        )
    )

    private _create = this._store.effect<{ ac: HotelComparableAssetClass, fileList: any[] }>(payload$ =>
        payload$.pipe(
            switchMap(({ ac, fileList }) =>
                this._service.store(ac, fileList).pipe(
                    tapResponse(
                        (response) => {
                            this._location.back()
                        },
                        (err) => {
                            console.error(err)
                        }
                    )
                )
            )
        )
    )

    private _update = this._store.effect<{ ac: HotelComparableAssetClass, fileList: any[] }>(payload$ =>
        payload$.pipe(
            switchMap(({ ac, fileList }) =>
                this._service.update(ac, fileList).pipe(
                    tapResponse(
                        (response) => {
                            this._location.back()
                        },
                        (err) => {
                            console.error(err)
                        }
                    )
                )
            )
        )
    )
}